import { customerExposer } from '@pirates/not-morty-biz';
import { ActionContext, ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { UsersState, UserState } from './user.model';
import { RootState } from '@/utils/types';
import { TRANSLATION_CONST } from '@/utils/translation';
import { defaultLocale } from '@/localization';
import { localStorage } from '@/utils/local-storage';
import { keycloak, keycloakB2B } from '@/services/auth/auth.service';
import { CompanyRoles } from '@/utils/permissions';
import { UserData, userService } from '@/services/user/user.service';

const LC_USE_B2B_AUTH_CLIENT = '_c2g_use_b2b_auth_client';

const persistLanguage = (localeString: string) => localStorage.set(TRANSLATION_CONST, localeString);

class Users implements Module<UsersState, RootState> {
  public state: UsersState = {
    consents: [],
    isBusinessAdmin: false,
    isLoggedIn: false,
    localConsents: [],
    newsletterStatus: {},
    paymentProfiles: [],
    roles: [],
    user: {
      country: '',
      language: '',
      legalEntityId: 10,
      uuid: '',
      firstName: '',
      lastName: '',
    },
    userProfile: {
      email: '',
    },
  };
  public namespaced: boolean = true;

  public actions = {
    async loadUserInformation(context: ActionContext<UsersState, RootState>) {
      try {
        const data = await customerExposer.customerData;
        const [language, country] = data.language.split('_');
        const user: Partial<UserData> = {
          ...data,
          language,
          country,
        };

        const localeString = localStorage.get(TRANSLATION_CONST);
        if (localeString) {
          user.language = localeString.split('-')[0];
        }

        if (keycloak.tokenParsed?.realm_access) {
          context.commit(
            'SET_USER_ROLES',
            keycloak.tokenParsed.realm_access.roles.filter((i) => CompanyRoles[i]),
          );
        }

        context.commit('SET_USER_INFORMATION', user);
        context.dispatch('setB2BAuthClient', false);
        context.commit('SET_USER_LOGGED_IN');
      } catch (err) {
        context.commit('CLEAR_USER_INFORMATION');
        throw err;
      }
    },

    async loadB2BUserInformation(context: ActionContext<UsersState, RootState>) {
      try {
        const { body: user } = await userService.get();
        const localeString = localStorage.get(TRANSLATION_CONST);
        if (localeString) {
          user.language = localeString.split('-')[0];
        }

        if (keycloakB2B.tokenParsed?.realm_access) {
          context.commit(
            'SET_USER_ROLES',
            keycloakB2B.tokenParsed.realm_access.roles.filter((i) => CompanyRoles[i]),
          );
        }

        context.commit('SET_USER_INFORMATION', user);
        context.dispatch('setB2BAuthClient', true);
        context.commit('SET_USER_LOGGED_IN');
      } catch (err) {
        context.commit('CLEAR_USER_INFORMATION');
        throw err;
      }
    },

    async loadUserProfile(context: ActionContext<UsersState, RootState>) {
      const userProfile = await customerExposer.customerProfile;
      context.commit('SET_USER_PROFILE', userProfile);
    },

    async loadPaymentProfiles(context: ActionContext<UsersState, RootState>) {
      const paymentProfiles = await customerExposer.paymentProfiles;
      context.commit('SET_PAYMENT_PROFILES', paymentProfiles);
      const isBusinessAdmin = await customerExposer.isBusinessAdmin;
      context.commit('SET_BUSINESS_USER', isBusinessAdmin);
    },
    async loadUserConsents(context: ActionContext<UsersState, RootState>, forceRefresh: boolean = false) {
      const { legalEntityId, uuid } = context.state.user as UserState;
      const consents = await customerExposer.getConsents(uuid, legalEntityId, forceRefresh);
      context.commit('SET_USER_CONSENTS', consents);
      context.commit('SET_LOCAL_USER_CONSENTS', consents);
    },
    async loadNewsletterStatus(context: ActionContext<UsersState, RootState>) {
      const newsletterStatus = await customerExposer.newsletterStatus;
      context.commit('SET_NEWSLETTER_STATUS', newsletterStatus);
    },

    setB2BAuthClient(_: ActionContext<UsersState, RootState>, useB2BAuthClient: boolean) {
      localStorage.set(LC_USE_B2B_AUTH_CLIENT, String(useB2BAuthClient));
    },

    updateConsentStatus(context: ActionContext<UsersState, RootState>, options) {
      context.commit('SET_CONSENT_STATUS', options);
    },
    updateUserLanguage(context: ActionContext<UsersState, RootState>, { isTemp, localeString }) {
      context.commit('SET_USER_LANGUAGE', { isTemp, localeString });
    },
    async saveConsents(context: ActionContext<UsersState, RootState>, { consents }) {
      const { legalEntityId, uuid } = context.state.user as UserState;
      await customerExposer.saveConsents(consents, { uuid, legalEntityId });

      context.commit('SET_USER_CONSENTS', context.state.localConsents);
    },
  } as ActionTree<UsersState, RootState>;

  public getters = {
    countryLang(state: UsersState) {
      const locale = `${state.user.country}/${state.user.language}`;
      return locale !== '/' ? locale : `DE/${defaultLocale.language}`;
    },
    country(state: UsersState) {
      return state.user.country || 'DE';
    },
    useB2BAuthClient(): boolean {
      return localStorage.get(LC_USE_B2B_AUTH_CLIENT) === 'true';
    },
    language(state: UsersState) {
      return state.user.language || defaultLocale.language;
    },
    myaccountURL(_, getters): string {
      return `${process.env.VUE_APP_BASE_URL}/${getters.countryLang}/myaccount`;
    },
  } as GetterTree<UsersState, RootState>;

  public mutations = {
    SET_USER_INFORMATION(state: UsersState, user: UserState | UserData) {
      state.user = user;
    },
    SET_USER_PROFILE(state: UsersState, userProfile) {
      state.userProfile = userProfile;
    },
    SET_USER_ROLES(state: UsersState, roles: CompanyRoles[]) {
      state.roles = roles;
    },
    SET_USER_LANGUAGE(state: UsersState, { isTemp, localeString }) {
      // store only language, without full locale
      state.user.language = localeString.split('-')[0];
      if (!isTemp) {
        persistLanguage(localeString);
      }
    },
    SET_USER_LOGGED_IN(state: UsersState) {
      state.isLoggedIn = true;
    },
    SET_NEWSLETTER_STATUS(state: UsersState, newsletterStatus) {
      state.newsletterStatus = newsletterStatus;
    },
    CLEAR_USER_INFORMATION(state: UsersState) {
      state.user = {
        language: '',
        country: '',
        legalEntityId: 10,
        uuid: '',
        firstName: '',
        lastName: '',
      };
      state.roles = [];
      state.isLoggedIn = false;
    },
    SET_PAYMENT_PROFILES(state: UsersState, paymentProfiles) {
      state.paymentProfiles = paymentProfiles;
    },
    SET_BUSINESS_USER(state: UsersState, isBusinessAdmin) {
      state.isBusinessAdmin = isBusinessAdmin;
    },
    SET_USER_CONSENTS(state: UsersState, consents) {
      state.consents = consents;
    },
    SET_LOCAL_USER_CONSENTS(state: UsersState, consents) {
      state.localConsents = consents;
    },
    SET_CONSENT_STATUS(state: UsersState, options) {
      state.localConsents = state.localConsents.map((consent: { slug: string }) => {
        if (consent.slug === options.slug) {
          return {
            ...consent,
            granted: options.status,
          };
        }

        return consent;
      });
    },
  } as MutationTree<UsersState>;
}

export default new Users();
